import { useCallback, useEffect, useRef, useState } from 'react';
import throttle from 'lodash/throttle';
import { useIsMobile } from '../../../utils/getIsMobile';
import { MOBILE_FOOTER_HEIGHT } from '../../dashboard/DashboardLayout';
import { VirtualAdaptiveGrid } from '../VirtualAdaptiveGrid/VirtualAdaptiveGrid';
import {
    MAX_COLUMNS,
    MIN_COLUMNS,
    MIN_ITEM_WIDTH,
    MIN_MOBILE_COLUMNS,
    MIN_MOBILE_WIDTH,
    THROTTLE_TIMEOUT,
    X_CARDS_MARGIN,
    X_CARDS_MOBILE_MARGIN,
} from './constants';
import { ResizableGridBox } from './styles';
import { IResizableGridProps } from './typings';

export const ResizableGrid = <ItemType extends { id: string }>(
    props: IResizableGridProps<ItemType>,
) => {
    const isMobile = useIsMobile();
    const {
        itemsYMargin = isMobile ? X_CARDS_MOBILE_MARGIN : X_CARDS_MARGIN,
        itemsXMargin = X_CARDS_MARGIN,
        bottomYMargin = MOBILE_FOOTER_HEIGHT,
        renderItem: renderItemBase,
        items,
    } = props;
    const defaultItemWidth = isMobile ? MIN_MOBILE_WIDTH : MIN_ITEM_WIDTH;
    const minItemWidth = props.minItemWidth || defaultItemWidth;
    const maxColumns = props.maxColumns || MAX_COLUMNS;
    const defaultMinColumns = isMobile ? MIN_MOBILE_COLUMNS : MIN_COLUMNS;
    const minColumns = props.minColumns || defaultMinColumns;
    const [columns, setColumns] = useState(0);
    const [itemWidth, setItemWidth] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    const renderItem = useCallback(
        (index: number, isVisible?: boolean, cardSize?: { width: number; height: number }) =>
            renderItemBase(index, columns, isVisible, cardSize),
        [columns, renderItemBase],
    );

    const changeItemWidth = useCallback(() => {
        const container = containerRef?.current;

        if (!container) {
            return;
        }

        let currentColumns = Math.min(
            Math.floor((container.clientWidth + itemsXMargin) / (minItemWidth + itemsXMargin)),
            maxColumns,
        );

        currentColumns = Math.max(currentColumns, minColumns);

        const newWidth = Number((container.clientWidth / currentColumns - itemsXMargin).toFixed(2));

        setItemWidth(newWidth);
    }, [minItemWidth, itemsXMargin, maxColumns, setItemWidth, minColumns]);

    const changeColumns = useCallback(() => {
        const container = containerRef?.current;

        if (!container || !itemWidth) {
            return;
        }

        const currentColumns = Math.floor(
            (container.clientWidth + itemsXMargin) / (itemWidth + itemsXMargin),
        );

        setColumns(Math.max(currentColumns, minColumns));
    }, [itemWidth, itemsXMargin, minColumns]);

    useEffect(() => {
        changeItemWidth();
        changeColumns();

        const onResize = throttle(() => {
            changeItemWidth();
            changeColumns();
        }, THROTTLE_TIMEOUT);

        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [changeItemWidth, changeColumns, items.length]);

    return (
        <ResizableGridBox ref={containerRef}>
            {columns ? (
                <VirtualAdaptiveGrid
                    {...props}
                    renderItem={renderItem}
                    key={`${columns}-${itemWidth}`}
                    itemWidth={itemWidth}
                    columns={columns}
                    itemsYMargin={itemsYMargin}
                    itemsXMargin={itemsXMargin}
                    bottomYMargin={bottomYMargin}
                />
            ) : null}
        </ResizableGridBox>
    );
};
